<template>
  <div class="uploader-container">
    <div ref="uploadingAlertRef" class="w-100" v-if="showUploadingAlert">
      <e-alert>
        <div class="paragraph-m_h font-600">Дождитесь загрузки всех фотографий</div>
      </e-alert>
    </div>

    <e-file-uploader
      v-model="images"
      multiple
      class="w-100"
      :accept="filesRules.extension"
      :delete-image-function="onImageDelete"
      :rules="filesRules"
      :disabled="totalFilesCount >= maxFilesLimit"
      @load="onImagesAdd"
    >
      <template #counter>
        <div class="files-counter" @click.stop>
          <span class="files-counter__text">{{ totalFilesCount }}/{{ maxFilesLimit }}</span>
          <help-tooltip :icon-size="16" @click.stop>
            <div class="files-counter__title">Загруженные файлы</div>
            <div class="files-counter__sections">
              <div v-for="section in displaySections" :key="section" class="files-counter__row">
                <span class="files-counter__row-label">{{ sectionNames[section] }}</span>
                <span class="files-counter__row-count">{{ getSectionFilesCount(section) }}</span>
              </div>
            </div>
          </help-tooltip>
        </div>
      </template>

      <template #warning>
        <div v-if="totalFilesCount >= maxFilesLimit" class="warning-text">
          <e-icon name="alert" class="warning-text__icon" :size="16" />
          <div class="warning-text__content">
            <div class="warning-text__title">Дальнейшая загрузка невозможна</div>
            <div class="warning-text__text">Вы достигли лимита загрузки файлов. Вы можете удалить часть загруженных файлов и добавить новые</div>
          </div>
        </div>
      </template>

      <template #subtitle>
        <div class="subtitle-content">
          Фотографии должны быть горизонтальными и не превышают {{ formatBytes(getConfigValue('contentServiceMaxUploadFileSize')) }}.
          <div>Суммарно в анкету вы можете загрузить не более 100 файлов</div>
        </div>
      </template>
    </e-file-uploader>

    <e-input v-model="imagesLink" label="Ссылка на фотобанк" :rules="[URLRule()]" />

    <remove-entity-confirmation 
      v-model="isRemoveEntityConfirmationOpen" 
      :action="handleConfirmDelete" 
    />
  </div>
</template>

<script lang="ts" setup>
import { ref, toRef, computed, watch, nextTick } from 'vue';
import type { Attachment } from '@/graphql/generated';
import { EFileUploader, EIcon, EInput, EAlert, HelpTooltip } from '@/core/components/ui';
import { getConfigValue } from '@/core/consts/config';
import { type LoadedFile } from '@/core/types/files';
import { formatBytes } from '@/core/utils/file';
import { URLRule } from '@/core/utils/validation';
import RemoveEntityConfirmation from '@/parts/content/components/RemoveEntityConfirmation.vue';
import { useContentStore } from '@/parts/content/store';
import { ContentSection } from '@/parts/content/types';
import { filesRules, useContentImagesUpload } from '@/parts/content/utils';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';

const props = withDefaults(
  defineProps<{
    contentSection: ContentSection;
    sectionObjectName?: string | null | undefined;
  }>(),
  { sectionObjectName: null },
);

const emit = defineEmits<{
  (e: 'uploaded'): void;
}>();

const { accommodationObject } = useAccommodationObjectsStore();
const { 
  questionnaire,
  totalFilesCount,
  maxFilesLimit,
  sectionNames,
  getSectionFilesCount,
  updateSectionFilesCount 
} = useContentStore();

const displaySections = [
  ContentSection.CommonInformation,
  ContentSection.Rooms,
  ContentSection.Equipment,
  ContentSection.FoodObjects,
  ContentSection.ConferenceRooms,
];

const images = defineModel<LoadedFile[]>({ required: true });
const imagesLink = defineModel<string | null | undefined>('imagesLink', { required: true });
const attachments = defineModel<Attachment[]>('attachments', { required: true });

const { uploadImages, deleteImage } = useContentImagesUpload(toRef(() => accommodationObject.value?.cid));

const isUploading = computed(() => {
  return images.value.some(image => 
    image.loading === true ||
    (image.file && !image.uploaded && !image.error)
  );
});

const showUploadingAlert = ref(false);
const uploadingAlertRef = ref();

const isRemoveEntityConfirmationOpen = ref(false);
const pendingDeletion = ref<{
  file: LoadedFile;
  attachment?: Attachment;
}>();

const scrollToAlert = () => {
  nextTick(() => {
    if (uploadingAlertRef.value) {
      uploadingAlertRef.value.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
    }
  });
};

const validate = (): boolean => {
  if (isUploading.value) {
    showUploadingAlert.value = true;
    scrollToAlert();
    return false;
  }
  return true;
};

const onImagesAdd = async () => {
  if (totalFilesCount.value > maxFilesLimit) {
    return;
  }
  
  await uploadImages(
    images.value,
    attachments.value,
    props.contentSection,
    props.sectionObjectName,
    questionnaire.value?.id,
  );

  emit('uploaded');
};

const onImageDelete = async (file: LoadedFile) => {
  const deletingAttachment = attachments.value.find((attachment) => attachment.id === file.id);

  if (deletingAttachment?.questionnaireId && deletingAttachment?.questionnaireId !== questionnaire.value?.id) {
    pendingDeletion.value = { file, attachment: deletingAttachment };
    isRemoveEntityConfirmationOpen.value = true;

    return false;
  }

  return await deleteImageFn(file, deletingAttachment);
};

const deleteImageFn = async (file: LoadedFile, attachment?: Attachment): Promise<boolean> => {
  if (!file) return false;

  file.loading = true;

  const result = await deleteImage(
    file.id,
    props.contentSection,
    props.sectionObjectName,
    attachments.value,
  );

  if (result) {
    images.value = images.value.filter((image) => image.id !== file.id);
  }

  file.loading = false;
  
  return result;
};

const handleConfirmDelete = async () => {
  if (pendingDeletion.value) {
    const { file, attachment } = pendingDeletion.value;
    await deleteImageFn(file, attachment);
    pendingDeletion.value = undefined;
  }
  isRemoveEntityConfirmationOpen.value = false;
};

watch(
  () => images.value.length,
  (newCount) => {
    updateSectionFilesCount(
      props.contentSection, 
      props.sectionObjectName,
      newCount
    );
  },
  { immediate: true }
);

watch(isUploading, (uploading) => {
  if (!uploading && showUploadingAlert.value) {
    showUploadingAlert.value = false;
  }
});

defineExpose({
  validate,
  isUploading
});
</script>

<style scoped lang="scss">
.uploader-container {
  display: flex;
  flex-direction: column;
  gap: 16px;

  width: 100%;
}

.files-counter__text {
  font-size: $paragraph-s-font-size;
  font-weight: 500;
  color: $text-ordinary-color;
  margin-right: 4px;
}

.warning-text {
  display: flex;
  gap: 8px;
  padding-left: 8px;
  padding-right: 8px;
}

.warning-text__icon {
  color: $alert-color;
}

.warning-text__title {
  font-size: $paragraph-xs-font-size;
  font-weight: 600;
  margin-bottom: 4px;
  color: $alert-color;
}

.warning-text__text {
  font-size: $paragraph-xs-font-size;
  color: $text-secondary-color;
}

.files-counter__title {
  font-size: $paragraph-s-font-size;
  font-weight: 600;
  color: $text-secondary-color;
  margin-bottom: 8px;
}

.files-counter__sections {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.files-counter__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 46px;
}

.files-counter__row-count {
  color: $text-primary-color;
}

.subtitle-content {
  text-align: center;
}
</style>
